import React, { useEffect, useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Card, CardContent } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Loader2, Search, UserCheck, UserX, Clock } from 'lucide-react';
import { useToast } from '@/hooks/use-toast';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { fetchEvent } from '@/store/eventSlice';
import { eventService } from '@/services/eventService';
import { mesaService } from '@/services/mesaService';
import { useEventPermission } from '@/hooks/useEventPermission';
import { AsistenciaStatus, Guest, Mesa } from '@/types';

const normalize = (s: string) =>
  s
    .toLowerCase()
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '');

const formatDate = (date?: Date) => {
  if (!date) return '';
  return date.toLocaleDateString('es-MX', { day: '2-digit', month: '2-digit', year: 'numeric' });
};

const formatTime = (date?: Date) => {
  if (!date) return '';
  return date.toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
};

const formaLabel = (forma: Mesa['forma']) =>
  forma === 'REDONDA' ? 'Redonda' : forma === 'CUADRADA' ? 'Cuadrada' : 'Rectangular';

export const EventReception: React.FC = () => {
  const { eventId } = useParams<{ eventId: string }>();
  const dispatch = useAppDispatch();
  const { selectedEvent, loading: eventLoading } = useAppSelector((s) => s.event);
  const { toast } = useToast();
  const { canWrite } = useEventPermission(eventId);

  const [guests, setGuests] = useState<Guest[]>([]);
  const [mesas, setMesas] = useState<Mesa[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selectedGuestId, setSelectedGuestId] = useState<string | null>(null);
  const [updating, setUpdating] = useState(false);

  useEffect(() => {
    if (eventId) dispatch(fetchEvent(eventId));
  }, [eventId, dispatch]);

  useEffect(() => {
    if (!eventId) return;
    let active = true;
    (async () => {
      try {
        setLoading(true);
        const [gs, ms] = await Promise.all([
          eventService.getEventGuests(eventId),
          mesaService.getEventMesas(eventId),
        ]);
        if (!active) return;
        setGuests(gs);
        setMesas(ms);
      } catch (err) {
        console.error('Error loading reception data:', err);
        if (active) {
          toast({
            title: 'Error',
            description: err instanceof Error ? err.message : 'No se pudieron cargar los datos',
            variant: 'destructive',
          });
        }
      } finally {
        if (active) setLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [eventId, toast]);

  const mesaById = useMemo(() => new Map(mesas.map((m) => [m.id, m])), [mesas]);

  const matches = useMemo(() => {
    const term = normalize(search.trim());
    if (term.length === 0) return [];
    return guests
      .filter((g) => normalize(g.nombre).includes(term))
      .slice(0, 5);
  }, [guests, search]);

  const selectedGuest = useMemo(
    () => guests.find((g) => g.id === selectedGuestId) ?? null,
    [guests, selectedGuestId],
  );
  const selectedMesa = selectedGuest?.mesa_id ? mesaById.get(selectedGuest.mesa_id) ?? null : null;

  const stats = useMemo(() => {
    const confirmados = guests.filter((g) => g.estado === 'CONFIRMADO').length;
    const presentes = guests.filter((g) => g.asistencia === 'PRESENTE').length;
    const pendientes = guests.filter(
      (g) => g.estado === 'CONFIRMADO' && g.asistencia === 'PENDIENTE',
    ).length;
    return { confirmados, presentes, pendientes };
  }, [guests]);

  const updateAttendance = async (asistencia: AsistenciaStatus) => {
    if (!selectedGuest) return;
    setUpdating(true);
    const prev = guests;
    setGuests((curr) =>
      curr.map((g) =>
        g.id === selectedGuest.id
          ? {
              ...g,
              asistencia,
              checked_in_at: asistencia === 'PRESENTE' ? new Date() : undefined,
            }
          : g,
      ),
    );
    try {
      await eventService.setGuestAttendance(selectedGuest.id, asistencia);
      toast({ title: 'Asistencia actualizada' });
    } catch (err) {
      console.error('Error setting attendance:', err);
      setGuests(prev);
      toast({
        title: 'Error',
        description: err instanceof Error ? err.message : 'No se pudo actualizar',
        variant: 'destructive',
      });
    } finally {
      setUpdating(false);
    }
  };

  const asistenciaBadge = (a: AsistenciaStatus) => {
    if (a === 'PRESENTE')
      return <Badge className="bg-emerald-100 text-emerald-800 border-emerald-200">Presente</Badge>;
    if (a === 'AUSENTE')
      return <Badge className="bg-red-100 text-red-800 border-red-200">Ausente</Badge>;
    return <Badge className="bg-amber-100 text-amber-800 border-amber-200">Pendiente</Badge>;
  };

  if (loading || eventLoading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <Loader2 className="w-8 h-8 animate-spin" />
      </div>
    );
  }

  if (!selectedEvent) {
    return (
      <div className="text-center py-8">
        <h2 className="text-xl font-semibold text-gray-900">Evento no encontrado</h2>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-3xl font-bold text-gray-900">Recepción</h1>
        <p className="text-gray-600 mt-1">
          {selectedEvent.nombre} · {formatDate(selectedEvent.fecha)}
        </p>
      </div>

      <div className="grid grid-cols-3 gap-3">
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-600">Confirmados</p>
            <p className="text-2xl font-bold text-gray-900">{stats.confirmados}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-600">Presentes</p>
            <p className="text-2xl font-bold text-emerald-600">{stats.presentes}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4 text-center">
            <p className="text-xs text-gray-600">Pendientes de llegar</p>
            <p className="text-2xl font-bold text-amber-600">{stats.pendientes}</p>
          </CardContent>
        </Card>
      </div>

      <Card>
        <CardContent className="p-4 sm:p-6">
          <label htmlFor="reception-search" className="block text-sm font-medium text-gray-700 mb-2">
            Buscar invitado
          </label>
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400" />
            <Input
              id="reception-search"
              autoFocus
              autoComplete="off"
              placeholder="Escribe el nombre del invitado..."
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setSelectedGuestId(null);
              }}
              className="pl-10 h-14 text-lg"
            />
          </div>

          {matches.length > 0 && !selectedGuest && (
            <div className="mt-4 space-y-2">
              {matches.map((g) => (
                <button
                  key={g.id}
                  type="button"
                  onClick={() => setSelectedGuestId(g.id)}
                  className="w-full text-left flex items-center justify-between gap-3 p-4 border rounded-lg hover:bg-blue-50 hover:border-blue-300 transition-colors"
                >
                  <div className="min-w-0">
                    <p className="text-lg font-semibold truncate">{g.nombre}</p>
                    <p className="text-sm text-gray-500">
                      {g.mesa_id ? `Mesa ${mesaById.get(g.mesa_id)?.numero ?? '?'}` : 'Sin mesa asignada'}
                      {g.numero_acompanantes > 0 && ` · +${g.numero_acompanantes}`}
                    </p>
                  </div>
                  {asistenciaBadge(g.asistencia)}
                </button>
              ))}
            </div>
          )}

          {search.trim().length > 0 && matches.length === 0 && !selectedGuest && (
            <p className="text-center text-gray-500 py-6">No se encontró ningún invitado.</p>
          )}
        </CardContent>
      </Card>

      {selectedGuest && (
        <Card className="border-blue-200">
          <CardContent className="p-6 sm:p-8">
            <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4 mb-6">
              <div>
                <p className="text-sm text-gray-500 uppercase tracking-wide">Invitado</p>
                <h2 className="text-3xl sm:text-4xl font-bold text-gray-900">
                  {selectedGuest.nombre}
                </h2>
                <div className="flex flex-wrap gap-2 mt-2">
                  {asistenciaBadge(selectedGuest.asistencia)}
                  {selectedGuest.numero_acompanantes > 0 && (
                    <Badge variant="secondary">
                      +{selectedGuest.numero_acompanantes} acompañante{selectedGuest.numero_acompanantes > 1 ? 's' : ''}
                    </Badge>
                  )}
                  {selectedGuest.checked_in_at && (
                    <Badge variant="outline">
                      Llegó {formatTime(selectedGuest.checked_in_at)}
                    </Badge>
                  )}
                </div>
              </div>
              <Button variant="ghost" onClick={() => setSelectedGuestId(null)}>
                Buscar otro
              </Button>
            </div>

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-6 text-center mb-6">
              {selectedMesa ? (
                <>
                  <p className="text-sm text-blue-700 uppercase tracking-wide">Mesa asignada</p>
                  <p className="text-7xl sm:text-8xl font-extrabold text-blue-900 mt-2">
                    {selectedMesa.numero}
                  </p>
                  {selectedMesa.nombre && (
                    <p className="text-xl text-blue-800 mt-1">{selectedMesa.nombre}</p>
                  )}
                  <p className="text-sm text-blue-700 mt-2">
                    Capacidad {selectedMesa.capacidad} · {formaLabel(selectedMesa.forma)}
                  </p>
                </>
              ) : (
                <>
                  <p className="text-2xl font-semibold text-amber-700">Sin mesa asignada</p>
                  <p className="text-sm text-amber-600 mt-1">
                    Asigna una mesa en la pestaña Mesas de Invitados.
                  </p>
                </>
              )}
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              <Button
                size="lg"
                className="h-16 bg-emerald-600 hover:bg-emerald-700"
                disabled={!canWrite || updating || selectedGuest.asistencia === 'PRESENTE'}
                onClick={() => updateAttendance('PRESENTE')}
              >
                <UserCheck className="w-5 h-5 mr-2" />
                Presente
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-16"
                disabled={!canWrite || updating || selectedGuest.asistencia === 'PENDIENTE'}
                onClick={() => updateAttendance('PENDIENTE')}
              >
                <Clock className="w-5 h-5 mr-2" />
                Pendiente
              </Button>
              <Button
                size="lg"
                variant="outline"
                className="h-16 text-red-600 border-red-200 hover:bg-red-50"
                disabled={!canWrite || updating || selectedGuest.asistencia === 'AUSENTE'}
                onClick={() => updateAttendance('AUSENTE')}
              >
                <UserX className="w-5 h-5 mr-2" />
                Ausente
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};
